import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, FormField, Input } from '@/components';
import { forgotPassword } from '../../api';
import {
  AUTH_BUTTON_CLASS,
  AUTH_FORM_CARD_CLASS,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
} from '../../lib/authStyles';

export function ForgotPasswordForm() {
  const [email, setEmail] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const navigate = useNavigate();
  const { mutate, isPending } = useMutation({
    mutationFn: forgotPassword,
    onSuccess: (data) => {
      setSuccessMsg(
        data?.message || 'Password reset link/OTP has been sent to your email.',
      );
      toast.success(data?.message || 'Verification code sent!');
      sessionStorage.removeItem('reset_flow_completed');
      navigate('/verify-otp', {
        replace: true,
        state: {
          mobile: email,
          flow: 'verification',
          expires_in_seconds: data?.expires_in_seconds,
        },
      });
      setEmail('');
    },
    onError: (err) => {
      const errorData = err.response?.data;
      if (
        errorData &&
        typeof errorData === 'object' &&
        !Array.isArray(errorData) &&
        !errorData.message &&
        !errorData.detail
      ) {
        const newErrors = {};
        Object.keys(errorData).forEach((key) => {
          let fieldName = key;
          if (key === 'email' || key === 'phone') fieldName = 'email';

          const errMsg = Array.isArray(errorData[key])
            ? errorData[key][0]
            : errorData[key];

          if (key === 'non_field_errors') {
            toast.error(errMsg);
          } else {
            newErrors[fieldName] = errMsg;
          }
        });
        setFieldErrors(newErrors);
      } else if (Array.isArray(errorData) && errorData.length > 0) {
        toast.error(errorData[0]);
      } else {
        toast.error(
          errorData?.message ||
            errorData?.detail ||
            'Failed to process forgot password request. Please try again.',
        );
      }
    },
  });

  const handleChange = (e) => {
    if (fieldErrors.email) setFieldErrors({ ...fieldErrors, email: '' });
    setEmail(e.target.value);
    if (successMsg) setSuccessMsg('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const targetVal = email.trim();
    if (!targetVal) {
      setFieldErrors({ email: 'Please enter your email address.' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(targetVal)) {
      setFieldErrors({ email: 'Please enter a valid email address.' });
      return;
    }
    mutate({ email: targetVal });
  };

  return (
    <div className={`${AUTH_FORM_CARD_CLASS} text-left`}>
      <form onSubmit={handleSubmit} className="w-full flex flex-col">
        <FormField
          label="Email Address"
          htmlFor="email"
          error={fieldErrors.email}
          className="mb-6"
          labelClassName={AUTH_LABEL_CLASS}
        >
          <Input
            size="lg"
            type="email"
            id="email"
            name="email"
            value={email}
            onChange={handleChange}
            placeholder="Enter your email address"
            error={!!fieldErrors.email}
            className={AUTH_INPUT_CLASS}
            disabled={isPending}
          />
        </FormField>

        {/* Submit Button */}
        <div className="flex justify-center mb-6">
          <Button
            size="lg"
            type="submit"
            variant="primary"
            className={`${AUTH_BUTTON_CLASS} w-[230px] h-[48px]`}
            disabled={isPending}
          >
            {isPending ? (
              <span className="flex items-center justify-center w-full">
                <svg
                  className="animate-spin h-5 w-5 text-surface-mint"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </span>
            ) : (
              <>
                <span>Send Forgot Code</span>
                <span>→</span>
              </>
            )}
          </Button>
        </div>

        {/* Back to Login */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              localStorage.clear();
              window.location.href = '/login';
            }}
            className="text-body-lg font-normal font-sf-pro-display text-text-primary hover:text-primary transition-colors cursor-pointer inline-flex items-center gap-1.5"
          >
            <svg
              className="w-4 h-4 inline"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5L8.25 12l7.5-7.5"
              />
            </svg>
            <span>Back to Login</span>
          </button>
        </div>
      </form>
    </div>
  );
}
